import { useEffect, useRef, useState } from 'react';
import '../../styles/components/baseComponents/_audioPlayer.scss';

function AudioPlayer({ audioUrl }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [hoverTime, setHoverTime] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0 });

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (audioRef.current.currentTime === duration) {
        audioRef.current.currentTime = 0;
      }
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSkipBackward = () => {
    audioRef.current.currentTime = Math.max(
      0,
      audioRef.current.currentTime - 5
    );
  };

  const handleSkipForward = () => {
    audioRef.current.currentTime = Math.min(
      audioRef.current.duration,
      audioRef.current.currentTime + 15
    );
  };

  const handleSpeedChange = () => {
    const newRate =
      playbackRate === 1.0 ? 1.5 : playbackRate === 1.5 ? 2.0 : 1.0;
    setPlaybackRate(newRate);
    audioRef.current.playbackRate = newRate;
  };

  const handleSeek = (e) => {
    const rect = e.target.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const newTime = (clickPosition / rect.width) * duration;
    audioRef.current.currentTime = newTime;
  };

  const handleMouseMove = (e) => {
    const rect = e.target.getBoundingClientRect();
    const hoverPosition = e.clientX - rect.left;
    const hoverTimeValue = (hoverPosition / rect.width) * duration;
    setHoverTime(hoverTimeValue);
    setTooltipPosition({ left: hoverPosition });
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
  };

  useEffect(() => {
    const handleTimeUpdate = () => {
      setCurrentTime(audioRef.current.currentTime);
    };
    const handleLoadedMetadata = () => {
      setDuration(audioRef.current.duration);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      audioRef.current.currentTime = 0;
    };
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
    }
    return () => {
      if (audio) {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
      }
    };
  }, [duration]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const progressPercentage = (currentTime / duration) * 100;

  return (
    <div className="audio-player">
      <div
        className="progress-container"
        onClick={handleSeek}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="progress-bar"
          style={{ width: `${progressPercentage}%` }}
        />
        {hoverTime !== null && (
          <div
            className="tooltip"
            style={{ left: `${tooltipPosition.left}px` }}
          >
            {formatTime(hoverTime)}
          </div>
        )}
      </div>
      <audio ref={audioRef} src={audioUrl} controls={false} />
      <div className="time-display flex justify-between controls">
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-500 flex items-center">
            <span>{formatTime(currentTime)}</span>
            <span className="mx-2">/</span>
            <span>{formatTime(duration)}</span>
          </div>
          <button className="control-speed-btn" onClick={handleSpeedChange}>
            {playbackRate}x
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button className="control-btn" onClick={handleSkipBackward}>
            <span className="icon">&#8634;</span>
          </button>
          <button
            className="control-play-pause-btn play-pause-btn"
            onClick={handlePlayPause}
          >
            {isPlaying ? (
              <span className="icon">&#10074;&#10074;</span>
            ) : (
              <span className="icon">&#9658;</span>
            )}
          </button>
          <button className="control-btn" onClick={handleSkipForward}>
            <span className="icon">&#8635;</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default AudioPlayer;
